<template>
  <v-app>
    <v-container fluid class="pa-0">
      <v-form @submit.prevent="submit">
        <v-row>
          <!-- ===================== ROSTER HEADER ===================== -->
          <v-col cols="12">
            <v-expansion-panels v-model="panel" multiple>
              <v-expansion-panel>
                <v-expansion-panel-title color="blue-grey-lighten-4">
                  {{ isEntryMode ? 'Create Roster' : 'Update Roster' }}
                </v-expansion-panel-title>

                <v-expansion-panel-text>
                  <v-card flat>
                    <v-card-text>
                      <v-row dense>
                        <!-- Department -->
                        <v-col cols="12" md="3">
                          <v-select
                            v-model="department_id.value.value"
                            :items="getHrmDepartmentList"
                            item-title="name"
                            item-value="id"
                            label="Department *"
                            variant="outlined"
                            density="compact"
                            clearable
                            :error-messages="department_id.errorMessage.value"
                          />
                        </v-col>

                        <!-- Employee -->
                        <v-col cols="12" md="3">
                          <v-select
                            v-model="employee_id.value.value"
                            :items="getHrmEmployeeList"
                            :item-title="item => `${item.code}-${item.name}` ?? 'N/A'"
                            item-value="id"
                            label="Employee"
                            variant="outlined"
                            density="compact"
                            clearable
                          />
                        </v-col>

                        <!-- From Date -->
                        <v-col cols="12" md="3">
                          <v-date-input
                            v-model="from_date.value.value"
                            label="From Date *"
                            variant="outlined"
                            density="compact"
                            prepend-inner-icon="mdi-calendar"
                            prepend-icon=""
                            clearable
                          />
                        </v-col>

                        <!-- To Date -->
                        <v-col cols="12" md="3">
                          <v-date-input
                            v-model="to_date.value.value"
                            label="To Date *"
                            variant="outlined"
                            density="compact"
                            prepend-inner-icon="mdi-calendar"
                            prepend-icon=""
                            clearable
                          />
                        </v-col>

                        <!-- Default Shift -->
                        <v-col cols="12" md="3">
                          <v-select
                            v-model="shift_id.value.value"
                            :items="getShiftList"
                            item-title="shift_name"
                            item-value="id"
                            label="Default Shift *"
                            variant="outlined"
                            density="compact"
                            clearable
                          />
                        </v-col>

                        <!-- Generate -->
                        <v-col cols="12" md="2">
                          <v-btn
                            color="primary"
                            variant="flat"
                            block
                            @click="generate"
                          >
                            Generate
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>

          <!-- ===================== ROSTER MAP ===================== -->
          <v-col cols="12">
            <v-expansion-panels v-model="panel2" multiple>
              <v-expansion-panel>
                <v-expansion-panel-title color="blue-grey-lighten-4">
                  Roster Map
                </v-expansion-panel-title>

                <v-expansion-panel-text>
                  <v-card flat>
                    <v-card-text>
                      <v-row
                        dense
                        v-for="(row, index) in rosterMap"
                        :key="index"
                        class="mb-2"
                      >
                        <v-col md="3">
                          <v-text-field
                            v-model="row.date"
                            label="Date"
                            variant="outlined"
                            density="compact"
                            readonly
                          />
                        </v-col>

                        <v-col md="3">
                          <v-select
                            v-model="row.employee_id"
                            :items="getHrmEmployeeList"
                            item-title="name"
                            item-value="id"
                            label="Employee"
                            variant="outlined"
                            density="compact"
                            clearable
                          />
                        </v-col>

                        <v-col md="2">
                          <v-select
                            v-model="row.shift_id"
                            :items="getShiftList"
                            item-title="shift_name"
                            item-value="id"
                            label="Shift"
                            variant="outlined"
                            density="compact"
                            clearable
                          />
                        </v-col>

                        <v-col md="2">
                          <v-select
                            v-model="row.type"
                            :items="rosterTypeList"
                            item-title="label"
                            item-value="value"
                            label="Roster Type"
                            variant="outlined"
                            density="compact"
                            clearable
                          />
                        </v-col>
                        <v-col md="2">
                          <v-btn icon variant="tonal" size="small" color="primary" @click="removeItem(index)">
                            <v-icon color="red">mdi-close</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- ACTION BUTTONS -->
            <v-divider class="my-3" />
            <v-card-actions class="justify-end">
              <v-btn color="grey" @click="submitForm(0)">Draft</v-btn>
              <v-btn color="primary" variant="flat" @click="submitForm(1)">
                {{ isEntryMode ? 'Save Roster' : 'Update Roster' }}
              </v-btn>
            </v-card-actions>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
  </v-app>
</template>

<script>
import { ref, onMounted } from "vue";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

export default {

    created() {
        this.loading = true;
        this.$store.dispatch('getShiftList').then((response) => {
            this.loading = false;
        });

        this.loading = true;
        this.$store.dispatch('getHrmDepartmentList').then((response) => {
            this.loading = false;
        });

        this.loading = true;
        this.$store.dispatch('getHrmEmployeeList').then((response) => {
            this.loading = false;
        })
  },

  computed: {
    getShiftList() {
      return this.$store.getters.getShiftList;
    },

    getHrmDepartmentList() {
      return this.$store.getters.getHrmDepartmentList;
    },

    getHrmEmployeeList() {
      return this.$store.getters.getHrmEmployeeList;
    },

  },

  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const panel = ref([0]);
    const panel2 = ref([0]);
    const isEntryMode = ref(true);
    const rosterMap = ref([]);

    const { handleSubmit, setFieldValue } = useForm({
      initialValues: {
        department_id: null,
        employee_id: null,
        shift_id: null,
        from_date: null,
        to_date: null,
        status: null,
      },
      validationSchema: yup.object({
        department_id: yup.number().required("Required"),
        from_date: yup.date().required("Required"),
        to_date: yup.date().required("Required"),
      }),
    });

    const department_id = useField("department_id");
    const employee_id = useField("employee_id");
    const shift_id = useField("shift_id");
    const from_date = useField("from_date");
    const to_date = useField("to_date");

    const rosterTypeList = [
      { label: "Normal", value: "normal" },
      { label: "Leave", value: "leave" },
      { label: "Weekly Off", value: "weekly_off" },
      { label: "Holiday", value: "holiday" },
      { label: "OT", value: "ot" },
    ];

    const removeItem= (i) => rosterMap.value.splice(i, 1);

    const generate = async () => {
      if (!department_id.value.value) return toast.error("Select Department");
      if (!from_date.value.value || !to_date.value.value)
        return toast.error("Select Date Range");

      try {
        const res = await store.dispatch("getDepartmentWiseEmployeeList", {
          department_id: department_id.value.value,
        });
        const employees = res.data.data || [];
        if (!employees.length) return toast.warning("No employee found");

        const start = new Date(from_date.value.value);
        const end = new Date(to_date.value.value);
        const temp = [];

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          const dateStr = `${yyyy}-${mm}-${dd}`;

          employees.forEach((emp) => {
            temp.push({
              date: formattedDate(dateStr),
              employee_id: emp.id,
              employee_name: emp.name,
              shift_id: shift_id.value.value ?? null,
              type: "normal",
            });
          });
        }

        rosterMap.value = temp;
      } catch {
        toast.error("Employee load failed");
      }
    };

    const submitForm = (status) => {
      setFieldValue("status", status);
      submit();
    };

    const submit = handleSubmit((values) => {
      values.from_date = formattedDate(from_date.value.value);
      values.to_date = formattedDate(to_date.value.value);
      const payload = {
        ...values,
        employee_shifts: rosterMap.value,
      };

      if (isEntryMode.value) {
        store
          .dispatch("saveRoster", payload)
          .then(() => {
            toast.success("Roster Saved");
            router.push({ name: "employee-roster-records" });
          });
      } else {
        const id = route.params.id;
        store
          store.dispatch("updateRoster", { id, data: payload })
          .then(() => {
            toast.success("Roster Updated");
            router.push({ name: "employee-roster-records" });
          });
      }
    });

    /* ================= LOAD MASTER DATA ================= */
    onMounted(async () => {
 

      const id = route.params.id;
      if (id) {
        isEntryMode.value = false;
        // Load roster data
        const res = await store.dispatch("getRosterById", { id });
        const data = res.data.data;

        department_id.value.value = data.department_id;
        employee_id.value.value = data.employee_id ?? null;
        shift_id.value.value = data.shift_id ?? null;
        from_date.value.value = data.from_date;
        to_date.value.value = data.to_date;

        rosterMap.value = data.details.map((row) => ({
          ...row,
          date: formattedDate(row.date),
        }));
      }
    });

    return {
      panel,
      panel2,
      isEntryMode,
      department_id,
      employee_id,
      shift_id,
      from_date,
      to_date,
      rosterMap,
      rosterTypeList,
      generate,
      submitForm,
      submit,
      removeItem,
      // getShiftList: store.getters.getShiftList,
      // getHrmDepartmentList: store.getters.getHrmDepartmentList,
      // getHrmEmployeeList: store.getters.getHrmEmployeeList,
    };
  },
};
</script>

<style scoped>
</style>
